// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LazyPicture from "@sc/MobiusOverrides/LazyImage/LazyPicture";
import { XNGAGE_CUSTOM_WIDGET } from "@sc/XDL/constants";
import parse from "html-react-parser";
import React from "react";

const enum fields {
    background = "background",
    desktopImage = "desktopImage",
    wideScreenImage = "wideScreenImage",
    tabletImage = "tabletImage",
    mobileImage = "mobileImage",
    backgroundColor = "backgroundColor",
    title = "title",
    overrideLinkText = "overrideLinkText",
    link = "link",
    openInNewWindow = "openInNewWindow",
    hideLink = "hideLink",
    height = "height",
}

interface Props extends WidgetProps {
    fields: {
        [fields.background]: "color" | "image";
        [fields.backgroundColor]: string;
        [fields.height]: string;
        [fields.wideScreenImage]: string;
        [fields.desktopImage]: string;
        [fields.tabletImage]: string;
        [fields.mobileImage]: string;
        [fields.title]: string;
        [fields.overrideLinkText]: string;
        [fields.link]: LinkFieldValue;
        [fields.openInNewWindow]: boolean;
        [fields.hideLink]: boolean;
    };
}

export interface PromotionalESpotStyles {
    container?: InjectableCss;
    contetnWrapper?: InjectableCss;
    pictureWrapper?: InjectableCss;
    title?: TypographyProps;
    link?: LinkPresentationProps;
}

export const promotionalESpotStyles: PromotionalESpotStyles = {};

const PromotionalESpot: React.FC<Props> = ({ id, fields }) => {
    const styles = promotionalESpotStyles;

    const {
        background,
        backgroundColor,
        height,
        wideScreenImage,
        desktopImage,
        tabletImage,
        mobileImage,
        link,
        overrideLinkText,
        openInNewWindow,
        title,
        hideLink,
    } = fields;

    const selectedBackgroundColor = background === "color" && backgroundColor;

    const linkDestination = useGetLink(link);
    let halfViewPort = false;
    let heightStyles = "";
    switch (height) {
        case "1/4 viewport":
            heightStyles = "min-height: 230px;";
            break;
        case "1/2 viewport":
            heightStyles = "min-height: 471px;";
            halfViewPort = true;
            break;
        case "3/4 viewport":
            heightStyles = "min-height: 600px;";
            break;
        case "fullViewport":
            heightStyles = "min-height: 800px;";
            break;
    }

    return (
        <StyledWrapper
            {...styles.container}
            {...{ heightStyles, halfViewPort, backgroundColor: fields.background === "color" && backgroundColor }}
        >
            {fields.background === "image" && (
                <StyledWrapper className="Promotional-Espot-image-wrapper" {...styles.pictureWrapper}>
                    <LazyPicture
                        extraLargeImageSrc={wideScreenImage}
                        largeImageSrc={desktopImage}
                        smallImageSrc={mobileImage || desktopImage}
                        mediumImageSrc={tabletImage || desktopImage}
                        altText="Promotional-Espot-image"
                    />
                </StyledWrapper>
            )}
            <StyledWrapper
                className="Promotional-Espot-content"
                {...styles.contetnWrapper}
                {...{ backgroundColor: selectedBackgroundColor }}
            >
                {title && (
                    <Typography className="PromotionalEspot-title" {...styles.title}>
                        {parse(title, parserOptions)}
                    </Typography>
                )}
                {!hideLink && (overrideLinkText || linkDestination.title) && (
                    <Link aria-label={overrideLinkText || linkDestination.title} href={linkDestination.url} target={openInNewWindow ? "_blank" : ""} {...styles.link}>
                        {overrideLinkText || linkDestination.title}
                    </Link>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const promotionalESpot: WidgetModule = {
    component: PromotionalESpot,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        fieldDefinitions: [
            {
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
                fieldType: "General",
                hideEmptyOption: true,
            },
            {
                fieldType: "Contextual",
                name: fields.wideScreenImage,
                displayName: "Widescreen Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "Contextual",
                name: fields.desktopImage,
                displayName: "Desktop Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "Contextual",
                name: fields.tabletImage,
                displayName: "Tablet Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tooltip: translate("preferred image width 1024px"),
            },
            {
                fieldType: "Contextual",
                name: fields.mobileImage,
                displayName: "Mobile Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tooltip: translate("preferred image width 768px"),
            },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#000000",
                isVisible: widget => widget.fields.background === "color",
                fieldType: "General",
            },
            {
                name: fields.height,
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/2 viewport",
                hideEmptyOption: true,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "RichTextField",
                fieldType: "General",
                defaultValue: "",
            },
            {
                name: fields.link,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                isRequired: true,
                fieldType: "General",
            },
            {
                name: fields.overrideLinkText,
                displayName: "override Link Text",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
            },
            {
                name: fields.openInNewWindow,
                displayName: "Open in New Window",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.hideLink,
                displayName: "Hide Link",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default promotionalESpot;
